<script setup lang="ts">
  import type {
    Client,
    Vendor,
    ChangeStakeholderInternalIdentificationNumbersRequestPayload
  } from '@gat-ship/types'
  import {
    testIds,
    isStakeholderClient,
    isStakeholderVendor
  } from '@gat-ship/types'
  import {
    FormField,
    FormSubmit,
    InputGroupInput,
    Button,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderDescription,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { ref } from 'vue'
  import { FieldGroup } from '@gat-ship/vue-components'
  import { useStakeholderContext } from '../../composables/useStakeholder'
  import type { ShowStakeholderUpdateInternalIdentificationNumbersParams } from '../providers/stakeholder-modals'
  import { useStakeholderMatchMessages } from '@gat-ship/stakeholder-create'
  import { text } from '@gat-ship/stakeholders'

  const isOpen = defineModel<boolean>('open', { default: false })
  const service = useStakeholderContext()

  const _stakeholder = ref<Client | Vendor | undefined>(undefined)
  const modalTitle = ref(
    text.groups.internalIdentificationNumbers('client').modals
      .editInternalIdentificationNumbers.title
  )
  const modalDescription = ref('')

  const { buildMatchMessages } = useStakeholderMatchMessages({
    excludeStakeholder: () => _stakeholder.value
  })

  const clientNumberMessagesAsync = buildMatchMessages(
    () => ({ type: 'client', name_matching: 'exact', client_number: '' }),
    { exactMatchMessage: 'This client number is already in use' }
  )
  const vendorNumberMessagesAsync = buildMatchMessages(
    () => ({ type: 'vendor', name_matching: 'exact', vendor_number: '' }),
    { exactMatchMessage: 'This vendor number is already in use' }
  )

  const internalIdSchema = z.object({
    clientNumber: z.string().optional(),
    vendorNumber: z.string().optional()
  })
  type InternalIdFormValues = z.infer<typeof internalIdSchema>

  const form = useForm({
    defaultValues: {
      clientNumber: '',
      vendorNumber: ''
    } as InternalIdFormValues,
    validators: {
      onChange: internalIdSchema
    },
    onSubmit: async ({ value }) => {
      const stakeholder = _stakeholder.value
      if (!stakeholder) {
        throw new Error('Stakeholder not found.')
      }

      let payload: ChangeStakeholderInternalIdentificationNumbersRequestPayload
      if (isStakeholderClient(stakeholder)) {
        payload = {
          clientNumber: value.clientNumber || null,
          vendorNumber: null
        }
      } else if (isStakeholderVendor(stakeholder)) {
        payload = {
          vendorNumber: value.vendorNumber || null,
          clientNumber: null
        }
      } else {
        throw new Error('Invalid stakeholder type.')
      }

      await service?.commands.changeInternalIdentificationNumbers(
        stakeholder.id,
        payload
      )
      isOpen.value = false
    }
  })

  function showModal(
    props: ShowStakeholderUpdateInternalIdentificationNumbersParams
  ) {
    _stakeholder.value = props.stakeholder
    modalTitle.value = text.groups.internalIdentificationNumbers(
      props.stakeholder.stakeholderType
    ).modals.editInternalIdentificationNumbers.title
    modalDescription.value = text.groups
      .internalIdentificationNumbers(props.stakeholder.stakeholderType)
      .modals.editInternalIdentificationNumbers.description(
        props.stakeholder?.name ?? ''
      )
    if (isStakeholderClient(props.stakeholder)) {
      form.reset({
        clientNumber: props.stakeholder?.clientNumber ?? '',
        vendorNumber: ''
      })
    } else if (isStakeholderVendor(props.stakeholder)) {
      form.reset({
        vendorNumber: props.stakeholder?.vendorNumber ?? '',
        clientNumber: ''
      })
    } else {
      throw new Error('Invalid stakeholder type.')
    }
    isOpen.value = true
  }

  defineExpose({ showModal })
</script>

<template>
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        {{ modalTitle }}
      </ModalHeaderTitle>
      <ModalHeaderDescription v-if="modalDescription">
        {{ modalDescription }}
      </ModalHeaderDescription>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <FieldGroup>
          <FormField
            v-if="_stakeholder && isStakeholderClient(_stakeholder)"
            :form="form"
            name="clientNumber"
            label="Client Number"
            :messages-async="clientNumberMessagesAsync"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.stakeholders.forms.internalIdentificationNumbers
                    .clientNumberInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
          <FormField
            v-if="_stakeholder && isStakeholderVendor(_stakeholder)"
            :form="form"
            name="vendorNumber"
            label="Vendor Number"
            hint="The vendor number is primarily used for integrations with external systems."
            :messages-async="vendorNumberMessagesAsync"
          >
            <template #default="{ field }">
              <InputGroupInput
                :id="field.name"
                :model-value="field.state.value ?? ''"
                :data-testid="
                  testIds.stakeholders.forms.internalIdentificationNumbers
                    .vendorNumberInput
                "
                @update:model-value="field.handleChange"
                @blur="field.handleBlur"
              />
            </template>
          </FormField>
        </FieldGroup>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save Changes" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
